import { trackEvent, AnalyticsEvents } from '../analytics/instance';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { Goal, CreateGoalData, UpdateGoalData, CompleteGoalData } from '../types';

// Хуки для целей
/** `enabled: false` — раздел «Цели» не подключён компании. */
export const useUserGoals = (userId: string, enabled = true) => {
  return useQuery({
    queryKey: ['goals', 'user', userId],
    queryFn: async (): Promise<Goal[]> => {
      return await api(`/goals/user/${userId}`);
    },
    enabled: !!userId && enabled,
  });
};

export const useAssignedGoals = (userId: string) => {
  return useQuery({
    queryKey: ['goals', 'assigned', userId],
    queryFn: async (): Promise<Goal[]> => {
      return await api(`/goals/assigned/${userId}`);
    },
    enabled: !!userId,
  });
};

export const useAllGoals = () => {
  return useQuery({
    queryKey: ['goals', 'all'],
    queryFn: async (): Promise<Goal[]> => {
      return await api('/goals/admin/all');
    },
  });
};

export const useGoal = (goalId: string) => {
  return useQuery({
    queryKey: ['goals', goalId],
    queryFn: async (): Promise<Goal> => {
      return await api(`/goals/${goalId}`);
    },
    enabled: !!goalId,
  });
};

export const useCreateGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateGoalData): Promise<Goal> => {
      return await api('/goals', {
        method: 'POST',
        body: data,
      });
    },
    onSuccess: (_, variables) => {
      trackEvent(AnalyticsEvents.GOAL_CREATED);
      // Инвалидируем кеши целей пользователя и поставленных целей
      queryClient.invalidateQueries({ queryKey: ['goals', 'user', variables.assigneeId] });
      queryClient.invalidateQueries({ queryKey: ['goals', 'assigned'] });
    },
  });
};

export const useUpdateGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      goalId,
      data,
    }: {
      goalId: string;
      data: UpdateGoalData;
    }): Promise<Goal> => {
      return await api(`/goals/${goalId}`, {
        method: 'PUT',
        body: data,
      });
    },
    onSuccess: () => {
      // Инвалидируем все кеши целей
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'goals',
      });
    },
  });
};

export const useAcceptGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (goalId: string): Promise<Goal> => {
      return await api(`/goals/${goalId}/accept`, {
        method: 'PATCH',
      });
    },
    onSuccess: () => {
      trackEvent(AnalyticsEvents.GOAL_STATUS_CHANGED, { status: 'accepted' });
      // Инвалидируем все кеши целей
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'goals',
      });
    },
  });
};

export const useCompleteGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      goalId,
      data,
    }: {
      goalId: string;
      data: CompleteGoalData;
    }): Promise<Goal> => {
      return await api(`/goals/${goalId}/complete`, {
        method: 'PATCH',
        body: data,
      });
    },
    onSuccess: () => {
      trackEvent(AnalyticsEvents.GOAL_COMPLETED);
      // Инвалидируем все кеши целей
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'goals',
      });
    },
  });
};

export const useDeleteGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (goalId: string): Promise<void> => {
      return await api(`/goals/${goalId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      // Инвалидируем все кеши целей
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'goals',
      });
    },
  });
};

export const useAddGoalComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ goalId, text }: { goalId: string; text: string }): Promise<Goal> =>
      api(`/goals/${goalId}/comments`, { method: 'POST', body: { text } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'goals' });
    },
  });
};
